import { Currency } from "@redotech/money/currencies";
import { Payer } from "@redotech/redo-model/return-flow";
import { ClientOptions, createRpcClient } from "@redotech/rpc/client";
import { InferRpcDefinition } from "@redotech/rpc/definition";
import { zExt } from "@redotech/rpc/ext";
import { z } from "zod";
import {
  AdvancedExchangeItemSchema,
  exchangeVariantSchema,
  NewExchangeItemSchema,
  PendingReturnItemSchema,
  ReturnableItemSchema,
  ReturnProductSchema,
} from "./line-item-schema";
import { fulfillmentOrderSchema } from "./schema/fulfillment-order-schema";
import { getSharedListRpcSchema } from "./schema/get-shared-list-schema";

export enum CompensationMethodType {
  EXCHANGE = "exchange",
  STORE_CREDIT = "storeCredit",
  REFUND = "refund",
  REPAIR = "repair",
}

export const COMPENSATION_TYPES = [
  CompensationMethodType.EXCHANGE,
  CompensationMethodType.STORE_CREDIT,
  CompensationMethodType.REFUND,
  CompensationMethodType.REPAIR,
];

export enum Platform {
  SHOPIFY = "shopify",
  COMMERCE_CLOUD = "commerce-cloud",
  COMMENTSOLD = "commentsold",
}

export const PLATFROM = [
  Platform.SHOPIFY,
  Platform.COMMERCE_CLOUD,
  Platform.COMMENTSOLD,
];

export const CompensationMethodSchema = z.object({
  type: z.nativeEnum(CompensationMethodType),
  // keys are pendingItemIds
  itemValues: z.record(
    z.object({
      strategy: z.enum(["refund", "storeCredit", "repair"]),
      value: zExt.big(),
      exchangeCredit: zExt.big(),
      isVariantExchange: z.boolean(),
      tax: zExt.big(),
    }),
  ),
  totalReturnableValue: zExt.big(),
  // keys are pendingItemIds
  adjustments: z.record(zExt.big()),
  shippingRequired: z.record(z.boolean()),
  manualReview: z.boolean(),
  manualReviewReason: z.string().nullish(),
  flagged: z.boolean(),
  flaggedReason: z.string().nullish(),
  payer: z.nativeEnum(Payer),
  returnMethodRequired: z.boolean(),
  exchangeShippingName: z.string().nullish(),
  chargeNewOrderShipping: z.boolean(),
  discountCodeName: z
    .object({
      prefix: z.string().nullish(),
      suffix: z.string().nullish(),
    })
    .nullish(),
  advancedExchangeAvailable: z.boolean(),
});

export type CompensationMethod = z.infer<typeof CompensationMethodSchema>;
export type CompensationType = CompensationMethod["type"];

export const InteractiveMapLocationSchema = z.object({
  name: z.string(),
  address: z.object({
    address1: z.string(),
    address2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  }),
  geo: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }),
  distance: z.string().optional(),
});

export type InteractiveMapLocation = z.infer<
  typeof InteractiveMapLocationSchema
>;

// Enum definitions
export const ExchangeTypeContext = z.enum(["VARIANT", "ADVANCED"]);
export const ProvisionType = z.enum(["instant", "processed"]);
export const PickupLocation = z.enum(["FRONT", "REAR", "SIDE", "NONE"]);
export const AdjustmentType = z.enum(["percentage", "amount"]);
export const ReturnOrClaimType = z.enum(["return", "claim"]);
export const ReturnRequestStatus = z.enum(["PENDING", "REJECTED"]);

// Schema definitions
const AddressSchema = z.object({
  name: z.string(),
  email: z.string(),
  street1: z.string(),
  street2: z.string().nullish(),
  state: z.string().nullish(),
  city: z.string(),
  country: z.string(),
  zip: z.string(),
  phone: z.string().nullish(),
  returnerName: z.string().nullish(),
  returnerEmail: z.string().nullish(),
  _id: z.string().nullish(),
});
export type Address = z.infer<typeof AddressSchema>;

const PickupDataSchema = z.object({
  email: z.string().nullish(),
  phone: z.string().nullish(),
  pickupDate: z.string(),
  pickupLocation: z.object({
    packageLocation: PickupLocation,
    specialInstructions: z.string().nullish(),
  }),
  textReminder: z.boolean(),
});
export type PickupData = z.infer<typeof PickupDataSchema>;

export enum ShipmentMethodName {
  PICKUP = "PICKUP",
  IN_STORE = "IN_STORE",
  SETTLEMENT = "SETTLEMENT",
  SELF_SHIP = "SELF_SHIP",
}
const ShipmentMethodSchema = z.union([
  z.object({
    method: z.literal(ShipmentMethodName.PICKUP),
    pickupData: PickupDataSchema,
  }),
  z.object({
    method: z.literal(ShipmentMethodName.IN_STORE),
  }),
  z.object({
    method: z.literal(ShipmentMethodName.SETTLEMENT),
  }),
  z.object({
    method: z.literal(ShipmentMethodName.SELF_SHIP),
  }),
]);
export type ShipmentMethod = z.infer<typeof ShipmentMethodSchema>;

const SettlementSchema = z.object({
  accepted: z.boolean(),
  refund: z.number(),
});

export const discountNameSchema = z.object({
  title: z.string(),
  code: z.string(),
});

const ShipmentAddressSchema = z.object({
  name: z.string().nullish(),
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  country: z.string().nullish(),
  country_name: z.string().nullish(),
  phone: z.string().nullish(),
  id: z.string().nullish(),
});
export type ShipmentAddress = z.infer<typeof ShipmentAddressSchema>;

export const DraftReturnShipmentSchema = z.object({
  carrier: z.string(),
  fee: z.string(),
  flatRateFee: z.string().nullish(),
  pickupFee: z.string().nullish(),
  pickupIneligibility: z.string().nullish(),
  address: ShipmentAddressSchema,
  pendingReturnItems: z.array(z.string()),
});

export type DraftReturnShipment = z.infer<typeof DraftReturnShipmentSchema>;

export enum ReturnTypeName {
  CLAIM = "claim",
  RETURN = "return",
  WARRANTY = "warranty",
}

const ReturnTotalCalculationsSchema = z.object({
  pickupAccept: z.boolean(),
  pickupFee: z.number().nullish(),
  inStoreAccept: z.boolean(),
  settlementAccept: z.boolean(),
  settlementRefund: z.number(),
  shippingFee: z.number(),
  totalShippingFee: z.number(),
  labelDeducted: z.boolean(),
  returnValue: z.number(),
  totalTaxes: z.number(),
  newOrderValue: z.number(),
  totalNewOrderTaxes: z.number(),
  totalAdjustmentValue: z.number(),
  totalReturnValue: z.number(),
  totalStoreCredit: z.number(),
  totalRefund: z.number(),
  totalRepairFee: z.number(),
  hasGreenReturnItems: z.boolean(),
  hasNonGreenReturnItems: z.boolean(),
  showNewItems: z.boolean(),
  paymentOwed: z.number(),
  skipPayment: z.boolean(),
});
export type ReturnTotalCalculations = z.infer<
  typeof ReturnTotalCalculationsSchema
>;

export const ReturnTotalCalculationsOutputSchema = z.object({
  instant: ReturnTotalCalculationsSchema,
  processed: ReturnTotalCalculationsSchema,
});
export type ReturnTotalCalculationsOutput = z.infer<
  typeof ReturnTotalCalculationsOutputSchema
>;

const OrderSchema = z.object({
  id: zExt.objectId(),
  warrantyId: zExt.objectId().nullish(),
  name: z.string(),
  tags: z.array(z.string()),
  orderDate: z.date(),
  totalPrice: z.string(),
  currency: z.string(),
  protected: z.boolean(),
  packageProtected: z.boolean(),
  finalSaleReturnsProtected: z.boolean(),
  productsCoveredByFinalSale: z.array(z.string()),
  paymentMethods: z.array(z.string()),
  salesChannel: z.string(),
  exchangeCount: z.number(),
  discounts: z.array(discountNameSchema),
  platform: z.nativeEnum(Platform),
  coverage: z.object({
    exchange: z.boolean(),
    storeCredit: z.boolean(),
    refund: z.boolean(),
    repair: z.boolean(),
  }),
});
export type Order = z.infer<typeof OrderSchema>;

// Main ReturnRequest schema
export const DraftReturnSchema = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  treatmentHash: z.string(),
  flow: z.any(),
  finalizeFlow: z.any(),
  returnType: z.nativeEnum(ReturnTypeName),
  orders: z.array(OrderSchema), // I wish this was a Record<string, OrderSchema>. I wish mongoose supported this in a way that wasn't a map. plz lmk if you know how to do this
  customer: z.object({
    shippingAddress: AddressSchema.nullish(),
    name: z.string(),
    tags: z.array(z.string()),
    numCustomerClaims: z.number(),
    numCustomerReturns: z.number(),
    numOrders: z.number(),
    returnRate: z.number(),
    newOrderAddress: AddressSchema.nullish(),
  }),
  newOrderItems: z.array(NewExchangeItemSchema),
  newOrderData: z
    .object({
      items: z.array(AdvancedExchangeItemSchema),
      totalPrice: z.string(), // without tax
      totalTax: z.string(),
      discountCodes: z.array(z.string()),
    })
    .nullish(),
  returnableItems: z.array(ReturnableItemSchema),
  pendingReturnItems: z.array(PendingReturnItemSchema),
  compensationMethods: z.array(CompensationMethodSchema),
  selectedMethod: z.nativeEnum(CompensationMethodType).nullish(),
  shipmentMethod: ShipmentMethodSchema.nullish(),
  shipments: z.array(DraftReturnShipmentSchema).nullish(),
  submitted: z.boolean().nullish(),
  provisionType: ProvisionType.nullish(),
  currency: z.nativeEnum(Currency).nullish(),
  presentmentCurrency: z.nativeEnum(Currency).nullish(),
});

export const returnItemDataSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
  collections: z.array(z.string()),
  deliveredOn: zExt.instant(),
  fulfilledOn: zExt.instant(),
  lastFulfillmentUpdateOn: zExt.instant(),
  productDiscounts: z.array(discountNameSchema),
  productProperties: z.record(z.string()),
  productTags: z.array(z.string()),
  productType: z.string(),
  productVariantMetafields: z.record(z.string()),
  sku: z.string(),
  variantData: z.object({
    productTitle: z.string(),
    tags: z.array(z.string()),
    options: z.array(
      z.object({
        position: z.number(),
        name: z.string(),
        values: z.array(z.string()),
      }),
    ),
    variants: z.array(exchangeVariantSchema),
  }),
});

const AddItemPendingItemSchema = PendingReturnItemSchema.omit({
  id: true,
  manualReview: true,
  flagged: true,
  returnReason: true,
  returnOptions: true,
  selectedReturnOption: true,
});
export type AddItemPendingItem = z.infer<typeof AddItemPendingItemSchema>;

export type ReturnItemData = z.infer<typeof returnItemDataSchema>;
export type ReturnRequest = z.infer<typeof DraftReturnSchema>;
export type DraftReturn = z.infer<typeof DraftReturnSchema>;
export type NewOrderData = z.infer<typeof DraftReturnSchema>["newOrderData"];

const changeAddressRpcSchema = {
  changeAddress: {
    input: z.object({
      draftReturnId: z.string(),
      newOrderAddress: AddressSchema.nullish(),
      returnAddress: AddressSchema.nullish(),
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
    }),
  },
};

const createDraftReturnRpcSchema = {
  createDraftReturn: {
    input: z.object({
      orderId: zExt.objectId().nullish(),
      email: z.string().nullish(),
      address: AddressSchema.nullish(),
      returnType: z.nativeEnum(ReturnTypeName),
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
    }),
  },
  createOrderlessDraftReturn: {
    input: z.object({
      productIds: z.array(z.string()),
      warrantyId: z.string().nullish(),
      returnType: z.nativeEnum(ReturnTypeName),
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
    }),
  },
};

const editReturnableItemRpcSchema = {
  editReturnableItem: {
    input: z.object({
      draftReturnId: zExt.objectId(),
      item: ReturnableItemSchema,
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
      newItemId: z.string(),
    }),
  },
};

const getDraftReturnRpcSchema = {
  getDraftReturn: {
    input: z.object({
      draftReturnId: zExt.objectId(),
    }),
    output: z.object({
      draftReturn: DraftReturnSchema.nullable(),
    }),
  },
};

const getShippingEstimateRpcSchema = {
  getShippingEstimate: {
    input: z.object({
      draftReturnId: zExt.objectId(),
    }),
    output: z.array(DraftReturnShipmentSchema),
  },
};

const getStripePublicKeyRpcSchema = {
  getStripePublicKey: {
    input: z.object({}).nullish(),
    output: z.object({
      publicKey: z.string(),
    }),
  },
};

const selectShipmentMethodRpcSchema = {
  selectShipmentMethod: {
    input: z.object({
      draftReturnId: zExt.objectId(),
      shipmentMethod: ShipmentMethodSchema,
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
    }),
  },
};

const setCompensationMethodRpcSchema = {
  setCompensationMethod: {
    input: z.object({
      draftReturnId: zExt.objectId(),
      selectedMethod: z.nativeEnum(CompensationMethodType),
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
    }),
  },
};

const addReturnItemRpcSchema = {
  addReturnItem: {
    input: z.object({
      draftReturnId: zExt.objectId(),
      previousPendingItemId: z.string().optional(),
      pendingItem: AddItemPendingItemSchema,
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
    }),
  },
};

const removeReturnItemRpcSchema = {
  removeReturnItem: {
    input: z.object({
      draftReturnId: zExt.objectId(),
      pendingItemId: z.string(),
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
    }),
  },
};

const submitReturnRpcSchema = {
  submitReturn: {
    input: z.object({
      draftReturnId: zExt.objectId(),
      settlement: SettlementSchema.nullish(),
      provisionType: ProvisionType,
      paymentToken: z.string().nullish(),
    }),
    output: z.object({
      returnId: z.string(),
    }),
  },
};

const submitReturnNewRpcSchema = {
  submitReturnNew: {
    input: z.object({
      draftReturnId: zExt.objectId(),
      settlement: SettlementSchema.nullish(),
      provisionType: ProvisionType,
      paymentToken: z.string().nullish(),
      idempotencyKey: z.string(),
    }),
    output: z.object({
      returnId: z.string(),
    }),
  },
};

const rejectReturnItemsRpcSchema = {
  rejectReturnItems: {
    input: z.object({
      draftReturnId: zExt.objectId(),
      returnableItemIds: z.array(z.string()),
      rejectReason: z.string(),
      rejectMessage: z.string(),
    }),
    output: z.object({}),
  },
};

const addNewItemsRpcSchema = {
  addNewItems: {
    input: z.object({
      draftReturnId: zExt.objectId(),
      items: z.array(NewExchangeItemSchema),
      clearExistingItems: z.boolean().optional(),
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
    }),
  },
};

const removeNewItemsRpcSchema = {
  removeNewItems: {
    input: z.object({
      draftReturnId: zExt.objectId(),
      variantIds: z.array(z.string()),
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
    }),
  },
};

const verifyAddressRpcSchema = {
  verifyAddress: {
    input: z.object({
      address: AddressSchema,
    }),
    output: z.object({
      verifiedAddress: AddressSchema,
      verifications: z.object({
        delivery: z.object({
          success: z.boolean(),
        }),
      }),
    }),
  },
};

const calculateReturnTotalsRpcSchema = {
  calculateReturnTotals: {
    input: z.object({
      draftReturnId: zExt.objectId(),
    }),
    output: z.object({
      calculations: ReturnTotalCalculationsOutputSchema,
    }),
  },
};

const getLocationsRpcSchema = {
  getLocations: {
    input: z.object({
      draftReturnId: zExt.objectId(),
    }),
    output: z.object({
      locations: z.array(InteractiveMapLocationSchema),
    }),
  },
};

const getOrderIdSchema = {
  getOrderId: {
    input: z.object({ shopifyId: z.string() }),
    output: z.object({ id: zExt.objectId() }),
  },
};

const toggleSelectReturnItemSchema = {
  toggleSelectReturnItem: {
    input: z.object({
      draftReturnId: z.string(),
      returnItemId: z.string(),
    }),
    output: z.object({
      draftReturn: DraftReturnSchema,
    }),
  },
};

const getSatisfactionResponseRpcSchema = {
  getSatisfactionResponse: {
    input: z.object({
      conversationId: z.string(),
    }),
    output: z.object({
      rating: z.number().nullish(),
      comment: z.string().nullish(),
      createdAt: z.date().nullish(),
    }),
  },
};

const saveSatisfactionResponseSchema = {
  saveSatisfactionResponse: {
    input: z.object({
      channel: z.string(),
      comment: z.string(),
      conversationId: z.string(),
      rating: z.number(),
    }),
    output: z.object({
      success: z.boolean(),
    }),
  },
};

const getWarrantyEligibleProductsSchema = {
  getWarrantyEligibleProducts: {
    input: z.object({}).nullish(),
    output: z.object({
      eligibleProducts: z.array(ReturnProductSchema),
    }),
  },
};

const getWarrantyRegistrationFlowSchema = {
  getWarrantyRegistrationFlow: {
    input: z.object({}).nullish(),
    output: z.object({
      draftWarrantyRegistration: DraftReturnSchema,
    }),
  },
};

const submitWarrantyRegistrationSchema = {
  submitWarrantyRegistration: {
    input: z.object({
      product: ReturnProductSchema,
      responses: z
        .array(
          z.object({
            type: z.enum(["images", "text_area", "multiple_choice", "videos"]),
            value: z.union([z.string(), z.array(z.string())]),
            message: z.string(),
          }),
        )
        .readonly()
        .nullish(),
      account: z.object({
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
        address: z.object({
          street1: z.string(),
          street2: z.string(),
          state: z.string(),
          city: z.string(),
          country: z.string(),
          zip: z.string(),
        }),
      }),
    }),
    output: z.object({
      success: z.boolean(),
      emailToken: z.string().nullish(),
      customer: z
        .object({
          name: z.string(),
          email: z.string(),
          customerId: z.string().nullish(),
        })
        .nullish(),
    }),
  },
};

export type SubmitWarrantyRegistrationInput = z.infer<
  (typeof submitWarrantyRegistrationSchema)["submitWarrantyRegistration"]["input"]
>;

type CustomerPortalRpcType = typeof changeAddressRpcSchema &
  typeof createDraftReturnRpcSchema &
  typeof editReturnableItemRpcSchema &
  typeof getDraftReturnRpcSchema &
  typeof getShippingEstimateRpcSchema &
  typeof getStripePublicKeyRpcSchema &
  typeof selectShipmentMethodRpcSchema &
  typeof setCompensationMethodRpcSchema &
  typeof addReturnItemRpcSchema &
  typeof removeReturnItemRpcSchema &
  typeof submitReturnRpcSchema &
  typeof submitReturnNewRpcSchema &
  typeof rejectReturnItemsRpcSchema &
  typeof addNewItemsRpcSchema &
  typeof removeNewItemsRpcSchema &
  typeof verifyAddressRpcSchema &
  typeof calculateReturnTotalsRpcSchema &
  typeof getLocationsRpcSchema &
  typeof getSatisfactionResponseRpcSchema &
  typeof saveSatisfactionResponseSchema &
  typeof getSharedListRpcSchema &
  typeof getWarrantyEligibleProductsSchema &
  typeof getWarrantyRegistrationFlowSchema &
  typeof submitWarrantyRegistrationSchema &
  typeof fulfillmentOrderSchema &
  typeof getOrderIdSchema &
  typeof toggleSelectReturnItemSchema;

export const customerPortalRPC: CustomerPortalRpcType = {
  ...changeAddressRpcSchema,
  ...createDraftReturnRpcSchema,
  ...editReturnableItemRpcSchema,
  ...getDraftReturnRpcSchema,
  ...getShippingEstimateRpcSchema,
  ...getStripePublicKeyRpcSchema,
  ...selectShipmentMethodRpcSchema,
  ...setCompensationMethodRpcSchema,
  ...addReturnItemRpcSchema,
  ...removeReturnItemRpcSchema,
  ...submitReturnRpcSchema,
  ...submitReturnNewRpcSchema,
  ...rejectReturnItemsRpcSchema,
  ...addNewItemsRpcSchema,
  ...removeNewItemsRpcSchema,
  ...verifyAddressRpcSchema,
  ...calculateReturnTotalsRpcSchema,
  ...getLocationsRpcSchema,
  ...getSatisfactionResponseRpcSchema,
  ...saveSatisfactionResponseSchema,
  ...getSharedListRpcSchema,
  ...getWarrantyEligibleProductsSchema,
  ...getWarrantyRegistrationFlowSchema,
  ...submitWarrantyRegistrationSchema,
  ...fulfillmentOrderSchema,
  ...getOrderIdSchema,
  ...toggleSelectReturnItemSchema,
};

export type CustomerPortalRpcDefiniton = InferRpcDefinition<
  typeof customerPortalRPC
>;

export const createCustomerPortalClient = (
  options: ClientOptions<typeof customerPortalRPC>,
) => createRpcClient(customerPortalRPC, options);

export type CustomerPortalClient = ReturnType<
  typeof createCustomerPortalClient
>;
